import { useState, useMemo, useRef, useCallback } from 'react'
import { Box, Radio, IconButton, Typography, Badge, Button, TextField, InputAdornment, CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { CustomPaper } from '../../../../../components/CustomPaper'
import BaseInput from '../../../../../components/dinamicForm/baseInput'
import { DynamicFormRef } from '../../../../../components/dynamicFormRef'
import { DinamicFormRefModel } from '../../../../../components/dynamicFormRef/types'
import { Item } from '../../../../../utils/types/generals'
import BaseModal from '../../../../../components/modal/baseModal'
import { icons } from '../../../../../utils/icons'
import { useContextState } from '../../../../../hooks/theme'
import { Step1UserWithoutStoreProps } from '../types'
import { useStyles } from '../styles'

function Step1UserWithoutStore({ planInfo, data, onChange, errors, storeFilterOptions, allAvailableUsers, onApplyFilters, isLoadingStores, isLoadingUsers, usersError }: Step1UserWithoutStoreProps) {
  const { t } = useTranslation()
  const styles = useStyles()
  const { colors } = useContextState()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const filterRef = useRef<any>(null)
  const [appliedFilters, setAppliedFilters] = useState<Record<string, Item[]>>({})
  const [tempFilters, setTempFilters] = useState<Record<string, Item[]>>({})

  const uniqueUnits = useMemo(() => {
    return storeFilterOptions.map((store) => ({
      id: String(store.id),
      value: String(store.id),
      label: store.label,
    }))
  }, [storeFilterOptions])

  const uniqueUserTypes = useMemo(() => {
    if (!allAvailableUsers.length) return []
    const types = new Map<number, Item>()
    allAvailableUsers.forEach((user) => {
      if (!types.has(user.userTypeId)) {
        types.set(user.userTypeId, {
          id: String(user.userTypeId),
          value: String(user.userTypeId),
          label: user.userType,
        })
      }
    })
    return Array.from(types.values())
  }, [allAvailableUsers])

  const uniqueRoles = useMemo(() => {
    if (!allAvailableUsers.length) return []
    const roles = new Map<number, Item>()
    allAvailableUsers.forEach((user) => {
      if (!roles.has(user.roleId)) {
        roles.set(user.roleId, {
          id: String(user.roleId),
          value: String(user.roleId),
          label: user.role,
        })
      }
    })
    return Array.from(roles.values())
  }, [allAvailableUsers])

  const filterModel = useMemo<Record<string, DinamicFormRefModel>>(() => ({
    unit: {
      id: 'unit',
      type: 'multiSelect',
      label: t('actionPlan.unit'),
      name: 'unit',
      items: uniqueUnits,
    },
    userType: {
      id: 'userType',
      type: 'multiSelect',
      label: t('actionPlan.userType'),
      name: 'userType',
      items: uniqueUserTypes,
    },
    role: {
      id: 'role',
      type: 'multiSelect',
      label: t('actionPlan.role'),
      name: 'role',
      items: uniqueRoles,
    },
  }), [uniqueUnits, uniqueUserTypes, uniqueRoles, t])

  const activeFilterCount = useMemo(() => {
    let count = 0
    for (const key in appliedFilters) {
      if (appliedFilters[key]?.length > 0) {
        count += appliedFilters[key].length
      }
    }
    return count
  }, [appliedFilters])

  const filteredUsers = useMemo(() => {
    if (!planInfo.availableUsers) return []

    let result = planInfo.availableUsers

    // Filtro por búsqueda de texto (local)
    if (searchTerm.trim()) {
      result = result.filter((user) =>
        user.fullName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    return result
  }, [planInfo.availableUsers, searchTerm])

  const handleSelectUser = (userId: number) => {
    onChange({ selectedUser: userId })
  }

  const handleFilterButton = useCallback(() => {
    const values = filterRef.current?.getValues ? filterRef.current.getValues() : tempFilters
    onApplyFilters({
      stores: values.unit?.map((item: Item) => Number(item.value)),
      userTypes: values.userType?.map((item: Item) => Number(item.value)),
      roles: values.role?.map((item: Item) => Number(item.value)),
    })
    setAppliedFilters({
      unit: values.unit || [],
      userType: values.userType || [],
      role: values.role || [],
    })
    setFilterModalOpen(false)
  }, [tempFilters, onApplyFilters])

  const hasSelectedFilters = useMemo(() => {
    return !!(
      tempFilters.unit?.length ||
      tempFilters.userType?.length ||
      tempFilters.role?.length
    )
  }, [tempFilters])

  const handleOpenFilterModal = () => {
    setFilterModalOpen(true)
    if (filterRef.current?.reset) {
      filterRef.current.reset(appliedFilters)
    }
  }

  const handleCloseModal = () => {
    setFilterModalOpen(false)
  }

  return (
    <Box sx={styles.step1TwoColumnContainer}>
      <Box sx={styles.step1LeftColumn}>
        <CustomPaper sx={styles.cardContainer} elevation={2}>
          <Box sx={styles.cardHeader}>
            <Typography sx={styles.cardTitle}>
              {t('actionPlan.assignedUserConfiguration')}
            </Typography>
            <Badge badgeContent={activeFilterCount} color="error">
              <IconButton
                sx={styles.userFilterButton}
                onClick={handleOpenFilterModal}
              >
                {icons('filter')}
              </IconButton>
            </Badge>
          </Box>

          <Box sx={styles.userSectionContainer}>
            <Box sx={styles.userSectionHeader}>
              <Typography sx={styles.userSectionTitle}>
                {t('actionPlan.users')}
              </Typography>
            </Box>

            <Box sx={styles.userListContainer}>
              <Box sx={{ 
                position: 'sticky', 
                top: 0, 
                zIndex: 2,
                backgroundColor: colors.color5,
              }}>
                <TextField
                  fullWidth
                  placeholder={t('actionPlan.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {icons('search')}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              {isLoadingUsers && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!isLoadingUsers && usersError && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 4 }}>
                  <Typography color="error">{t('actionPlan.errorLoadingPlanInfo')}</Typography>
                </Box>
              )}

              {!isLoadingUsers && !usersError && !planInfo.availableUsers?.length && (
                <Typography sx={styles.noData}>
                  {t('actionPlan.noAvailableUsers')}
                </Typography>
              )}

              {!isLoadingUsers && !usersError && filteredUsers.map((user) => {
                const isSelected = data.selectedUser === user.id
                return (
                  <Box key={user.id} sx={{ ...styles.userItem, cursor: 'pointer' }} onClick={() => handleSelectUser(user.id)}>
                    <Radio
                      checked={isSelected}
                      onChange={() => handleSelectUser(user.id)}
                      color='primary'
                      sx={styles.userCheckbox}
                    />
                    <Box sx={styles.userItemInfo}>
                      <Typography sx={styles.userItemName}>{user.fullName}</Typography>
                      <Typography sx={styles.userItemDetail}>
                        {t('actionPlan.role')}: {user.role} - {t('actionPlan.userType')}: {user.userType}
                      </Typography>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Box>
        </CustomPaper>
      </Box>

      <Box sx={styles.step1RightColumn}>
        <CustomPaper sx={styles.cardContainer} elevation={2}>
          <Typography sx={styles.cardTitle}>
            {t('actionPlan.actionPlanInformation')}
          </Typography>

          <Box sx={{ marginTop: '20px' }}>
            <BaseInput
              name='title'
              value={{
                title: {
                  label: t('actionPlan.actionPlanTitle'),
                  value: data.title,
                },
              }}
              onChange={({ target }) => {
                onChange({ title: target.value as string })
              }}
              errors={errors}
              required
            />
          </Box>

          <Box sx={{ marginTop: '16px', paddingTop: '12px', flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            <TextField
              multiline
              minRows={4}
              label={t('actionPlan.instruction')}
              value={data.description}
              onChange={(e) => onChange({ description: e.target.value })}
              error={!!errors.description}
              helperText={errors.description}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                '& .MuiInputBase-root': {
                  overflow: 'auto',
                  flex: 1,
                  alignItems: 'flex-start',
                },
              }}
            />
          </Box>
        </CustomPaper>
      </Box>

      <BaseModal
        sx={{ borderRadius: '0px' }}
        open={filterModalOpen}
      >
        <Box sx={styles.filterModalContainer}>
          <Box sx={styles.filterModalHeader}>
            <Typography sx={styles.filterModalTitle}>
              {t('actionPlan.filterModalTitle')}
            </Typography>
            <IconButton
              onClick={handleCloseModal}
              sx={styles.filterModalClose}
            >
              {icons('simple-close')}
            </IconButton>
          </Box>

          <DynamicFormRef
            ref={filterRef}
            model={filterModel}
            defaultValues={appliedFilters}
            onChange={(values) => {
              setTempFilters(values as Record<string, Item[]>)
            }}
            parentWrapper={({ children }) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {children}
              </Box>
            )}
          />

          <Button
            variant='contained'
            onClick={handleFilterButton}
            disabled={!hasSelectedFilters || isLoadingStores}
            sx={styles.filterModalButton}
          >
            {t('actionPlan.filter')}
          </Button>
        </Box>
      </BaseModal>
    </Box>
  )
}

export default Step1UserWithoutStore
